import { Injectable } from "@angular/core";
import { LegacyMeasurementService, Measurement } from "@evident/ngx-media-viewer";
import { MetadataService } from "@evident/ngx-metadata-writer";
import { MerryMeasurementMetadata } from "./merry-measurement-metadata";

export interface ParseMeasurementOptions {
  /**
   * Whether the device measurements of an image that holds no ViSOL / Merry measurements (IPLEX NX, IPLEX One) are
   * imported into the measurement state. The measurement tab wants them (they are re-measured through the measurement
   * service and drawn); a report must neither touch the state nor call the service. Defaults to true.
   */
  importLegacy?: boolean;
}

@Injectable({ providedIn: "root" })
export class MerryMeasurementParserService {
  private metadataMeasurementTag = "Measurements";

  constructor(
    private metadataService: MetadataService,
    private _legacyMeasurementService: LegacyMeasurementService
  ) {
    this._legacyMeasurementService.setConfig({ requireImportConfirmation: false });
  }

  async parseMeasurementResults(imagePath: string, options: ParseMeasurementOptions = {}): Promise<Measurement[]> {
    const [cleanPath] = imagePath.split("?"); // Remove query parameters for cache busting
    const label = `[MerryMeasurement] ${decodeURIComponent(cleanPath.split(/[\\/]/).pop() ?? cleanPath)}`;

    const measurements = await this.extractMeasurementFromMetadata(cleanPath, imagePath, label);
    if (!measurements.length && (options.importLegacy ?? true)) {
      await this.extractLegacyMeasurementFromMetadata(cleanPath);
    }

    return measurements;
  }

  private async extractLegacyMeasurementFromMetadata(imageSrc: string) {
    const legacyMetadata = await this.metadataService.extractLegacyMetadata(imageSrc);

    // Subject to change when adressing this ticket: VISOL-2623
    await this._legacyMeasurementService.readLegacyMeasurements(this.deepJsonParse(legacyMetadata), imageSrc);
  }

  private async extractMeasurementFromMetadata(imageSrc: string, viewerId: string, label: string): Promise<Measurement[]> {
    const metadata = await this.metadataService.extractMetadata(imageSrc);

    if (!metadata[this.metadataMeasurementTag]) {
      return [];
    }

    try {
      const stored: unknown = JSON.parse(metadata[this.metadataMeasurementTag]);
      // The stored viewerId is the image URL of the session that measured. The media viewer only
      // draws and edits measurements whose viewerId equals its current [src], so re-stamp it with
      // the URL of this session: the stored one is stale as soon as the file moves (sync to
      // another machine, export/re-import, online temp copy).
      return MerryMeasurementMetadata.toMeasurements(stored, viewerId, label);
    } catch (e) {
      console.error(`${label}: failed to parse measurements from metadata`, metadata[this.metadataMeasurementTag], e);
      return [];
    }
  }

  private deepJsonParse<T>(input: unknown): T {
    function parseValue(val: unknown): unknown {
      let result = val;
      while (typeof result === "string") {
        try {
          result = JSON.parse(result);
        } catch {
          break;
        }
      }
      if (Array.isArray(result)) {
        return result.map(parseValue);
      }
      if (result && typeof result === "object") {
        const obj: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(result)) {
          obj[key] = parseValue(value);
        }
        return obj;
      }
      return result;
    }
    return parseValue(input) as T;
  }
}
